import React, { FC, useEffect, useRef, useState } from 'react';

import { LoadingOutlined, LockOutlined } from '@ant-design/icons';
import { Button, Result, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { getAccessToken } from '../../../../helpers/utils/auth';
import { showErrorMessage } from '../../../../helpers/utils/ui';
import { unApi } from '../../api/endpoints/reports/unApi';
import { API_GET_EXPORT } from '../../api/urls';
import { useTypedSelector } from '../../hooks/useTypedSelector';

// Проверка code: 221 — может прийти как в success (HTTP 200), так и в error (HTTP 4xx)
const isAccessDenied = (data: any): boolean => {
    return data?.code === 221;
};

const ExportPage: FC = () => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);

    const [ tableData, setTableData ] = useState([]);
    const [ tableLoading, setTableLoading ] = useState(false);
    const [ accessDenied, setAccessDenied ] = useState(false);
    // Ref для проверки доступа внутри замыканий (state не обновляется в замыкании)
    const accessDeniedRef = useRef(false);
    // Ref для отмены таймера при размонтировании
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);

    const downloadFile = (id: string) => {
        if (selectedSchool?.id) {
            const url = `${API_GET_EXPORT}/${
                selectedSchool.id
            }/${id}/${getAccessToken()}`;
            const link = document.createElement('a');

            link.href = url;

            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const columns: ColumnsType<any> = [
        {
            title: 'Дата создания',
            dataIndex: 'created_at',
            key: 'created_at',
        },
        {
            title: 'Название файла',
            dataIndex: 'export_name',
            key: 'export_name',
        },
        {
            title: 'Файл',
            dataIndex: 'status',
            key: 'status',
            render: (url, record) => {
                // state приходит с бэка: ready | pending | failed. Если поля нет (старый
                // бэк), определяем состояние по url, как было раньше.
                const state = record?.state ?? (url?.includes('Файл готовится') ? 'pending' : 'ready');

                if (state === 'ready') {
                    const id = url.split('id=')[1];

                    return (
                        <Button type="link" onClick={() => downloadFile(id)}>
                            Скачать файл
                        </Button>
                    );
                }

                if (state === 'failed') {
                    return (
                        <Tooltip title="Файл не был сформирован. Закажите выгрузку заново — если период большой, уменьшите его или уберите лишние группировки.">
                            <span style={{ color: '#ff4d4f' }}>Не удалось сформировать</span>
                        </Tooltip>
                    );
                }

                return <span>Файл готовится...</span>;
            },
        },
        {
            title: 'Кто создал',
            dataIndex: 'user_name',
            key: 'user_name',
        },
        {
            title: 'Дата готовности файла',
            dataIndex: 'updated_at',
            key: 'updated_at',
        },
    ];

    const fetchData = () => {
        // Если доступ запрещён — не делаем повторных запросов
        if (accessDeniedRef.current) return;

        setTableLoading(true);

        unApi
            .getExports(selectedSchool?.id || '')
            .then((response) => {
                if (!isMountedRef.current) return;

                // Проверяем ответ с кодом ошибки доступа (HTTP 200, code: 221)
                if (isAccessDenied(response.data)) {
                    accessDeniedRef.current = true;
                    setAccessDenied(true);
                    setTableLoading(false);
                    showErrorMessage('Недостаточно прав для просмотра экспортов. Обратитесь к владельцу школы.');
                    return;
                }

                if (response.status === 200) {
                    setTableData(response.data);
                } else {
                    setTableData([]);
                }
            })
            .catch((err) => {
                if (!isMountedRef.current) return;

                // Проверяем code: 221 в теле ошибки (HTTP 4xx с {code: 221} в body)
                if (isAccessDenied(err?.response?.data)) {
                    accessDeniedRef.current = true;
                    setAccessDenied(true);
                    setTableLoading(false);
                    showErrorMessage('Недостаточно прав для просмотра экспортов. Обратитесь к владельцу школы.');
                    return;
                }

                if (err?.response?.status !== 401) {
                    const serverMessage = err?.response?.data?.message;
                    showErrorMessage(serverMessage || 'Ошибка при получении данных');
                }
            })
            .finally(() => {
                if (!isMountedRef.current) return;
                setTableLoading(false);

                // Повторный запрос только если доступ не запрещён и компонент ещё смонтирован
                if (!accessDeniedRef.current && isMountedRef.current) {
                    timerRef.current = setTimeout(() => fetchData(), 10000);
                }
            });
    };

    useEffect(() => {
        isMountedRef.current = true;
        fetchData();

        // Очистка: останавливаем таймер при размонтировании
        return () => {
            isMountedRef.current = false;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    if (accessDenied) {
        return (
            <div className="app">
                <Result
                    icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
                    title="Нет доступа"
                    subTitle="У вас недостаточно прав для просмотра экспортов. Обратитесь к владельцу школы для получения доступа."
                />
            </div>
        );
    }

    return (
    <div className="app">
      <div style={{ width: '100%' }}>
        <Table
            dataSource={tableData}
            columns={columns}
            loading={{
                indicator: <LoadingOutlined />,
                spinning: tableLoading,
            }}
            pagination={{
                position: [ 'bottomLeft' ],
            }}
        />
      </div>
    </div>
    );
};

export default ExportPage;
